/**
 * Tenant-specific rent/deposit discounts (COL-360).
 *
 * MIRRORED from supabase/functions/_shared/rent-discount.ts — keep the two in
 * sync. Same reason as card-fees.ts and management-invoice-lines.ts: edge
 * functions run on Deno and cannot import from `src/`.
 *
 * The distinction this module exists to hold:
 *
 *   BASE      what the unit costs. `rooms.price` / `rooms.deposit_amount`,
 *             mirrored onto the booking as the amount agreed with this tenant.
 *   DISCOUNT  what THIS tenant pays less, for a bounded number of months or for
 *             the whole term. Lives only on the booking.
 *
 * `bookings.monthly_rent` therefore stays the contractual base and every
 * consumer that needs "what does this tenant owe for October" calls
 * effectiveRentForMonthEUR instead of reading the column directly. A single
 * stored figure could not express a discount that ends: after month three the
 * amount has to go back up on its own, with no job to flip it and no ambiguity
 * about what past invoices meant.
 *
 * Mode is stored, not a resolved euro amount, so a percentage discount tracks
 * the base instead of freezing into a stale figure when the base changes.
 */

import { roundRentEUR } from "./rent";

export type DiscountMode = "eur" | "pct";

/** The discount-carrying subset of a booking row. */
export interface DiscountBookingFields {
  monthly_rent?: number | null;
  deposit_amount?: number | null;
  rooms?: { price?: number | null; deposit_amount?: number | null } | null;
  rent_discount_mode?: string | null;
  rent_discount_value?: number | null;
  rent_discount_months?: number | null;
  rent_discount_start_month?: string | null;
  deposit_discount_mode?: string | null;
  deposit_discount_value?: number | null;
  discount_reason?: string | null;
}

const MONTH_RE = /^\d{4}-\d{2}(-\d{2})?$/;

/** `YYYY-MM-01` for any date-ish string, or null when unparseable. */
export function monthStart(dateish: string | null | undefined): string | null {
  if (!dateish) return null;
  const s = String(dateish).slice(0, 10);
  if (!MONTH_RE.test(s)) return null;
  return `${s.slice(0, 7)}-01`;
}

/** Month string N months after `from`, e.g. addMonths("2026-11-01", 2) → "2027-01-01". */
export function addMonths(from: string, n: number): string {
  const y = Number(from.slice(0, 4));
  const m = Number(from.slice(5, 7));
  // Zero-based month arithmetic so December + 1 rolls the year over.
  const total = y * 12 + (m - 1) + n;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  return `${String(ny).padStart(4, "0")}-${String(nm).padStart(2, "0")}-01`;
}

function normalizeMode(mode: string | null | undefined): DiscountMode {
  // Anything unrecognised is treated as euros, matching the CHECK constraint's
  // default reading — but the constraint is what actually keeps junk out.
  return mode === "pct" ? "pct" : "eur";
}

function positive(n: number | null | undefined): number {
  const v = Number(n);
  return Number.isFinite(v) && v > 0 ? v : 0;
}

/**
 * Euro amount a discount takes off `baseEUR`, clamped to the base.
 *
 * Clamping matters: a €700 discount on €650 rent must bill €0, never a negative
 * amount that would read as a credit the tenant is owed.
 */
export function discountOffEUR(
  baseEUR: number,
  mode: string | null | undefined,
  value: number | null | undefined
): number {
  const base = positive(baseEUR);
  const raw = positive(value);
  if (base <= 0 || raw <= 0) return 0;
  const off = normalizeMode(mode) === "pct" ? base * (raw / 100) : raw;
  return roundRentEUR(Math.min(base, off));
}

/** Base (pre-discount) monthly rent: the booking's agreed rent, else the room's. */
export function baseRentEUR(booking: DiscountBookingFields | null): number {
  if (!booking) return 0;
  const r = positive(booking.monthly_rent);
  if (r > 0) return roundRentEUR(r);
  return roundRentEUR(positive(booking.rooms?.price));
}

/** Base (pre-discount) deposit: the booking's agreed deposit, else the room's base, else 2x rent. */
export function baseDepositEUR(booking: DiscountBookingFields | null): number {
  if (!booking) return 0;
  const agreed = positive(booking.deposit_amount);
  if (agreed > 0) return roundRentEUR(agreed);
  const roomBase = positive(booking.rooms?.deposit_amount);
  if (roomBase > 0) return roundRentEUR(roomBase);
  const rent = baseRentEUR(booking);
  return rent > 0 ? roundRentEUR(rent * 2) : 0;
}

export function hasRentDiscount(
  booking: DiscountBookingFields | null
): boolean {
  return positive(booking?.rent_discount_value) > 0;
}

export function hasDepositDiscount(
  booking: DiscountBookingFields | null
): boolean {
  return positive(booking?.deposit_discount_value) > 0;
}

/** True when the discount runs for the whole tenancy rather than N months. */
export function isFullTermRentDiscount(
  booking: DiscountBookingFields | null
): boolean {
  if (!hasRentDiscount(booking)) return false;
  const months = booking?.rent_discount_months;
  return months == null || !(Number(months) > 0);
}

/**
 * Inclusive first/last month the rent discount covers.
 *
 * `end` is null for a full-term discount. A discount with no anchor month is
 * treated as full-term rather than guessing a start: guessing would silently
 * bill the wrong months, while full-term is at least visible in the summary.
 */
export function rentDiscountWindow(booking: DiscountBookingFields | null): {
  start: string | null;
  end: string | null;
} {
  if (!hasRentDiscount(booking)) return { start: null, end: null };
  const start = monthStart(booking?.rent_discount_start_month);
  if (!start) return { start: null, end: null };
  const months = Number(booking?.rent_discount_months);
  if (!(months > 0)) return { start, end: null };
  return { start, end: addMonths(start, Math.floor(months) - 1) };
}

/** Does the rent discount apply to `monthish` (any date inside the month)? */
export function rentDiscountCoversMonth(
  booking: DiscountBookingFields | null,
  monthish: string | null | undefined
): boolean {
  if (!hasRentDiscount(booking)) return false;
  const m = monthStart(monthish);
  if (!m) return false;
  const { start, end } = rentDiscountWindow(booking);
  // No anchor stored → full term, so every month is covered.
  if (!start) return true;
  if (m < start) return false;
  return end == null ? true : m <= end;
}

/** Euros off the rent for `monthish`; 0 outside the discount window. */
export function rentDiscountOffEURForMonth(
  booking: DiscountBookingFields | null,
  monthish: string | null | undefined
): number {
  if (!rentDiscountCoversMonth(booking, monthish)) return 0;
  return discountOffEUR(
    baseRentEUR(booking),
    booking?.rent_discount_mode,
    booking?.rent_discount_value
  );
}

/**
 * What this tenant actually owes for `monthish` — the number every money path
 * should use in place of `bookings.monthly_rent`.
 */
export function effectiveRentForMonthEUR(
  booking: DiscountBookingFields | null,
  monthish: string | null | undefined
): number {
  const base = baseRentEUR(booking);
  return roundRentEUR(
    Math.max(0, base - rentDiscountOffEURForMonth(booking, monthish))
  );
}

/** Euros off the deposit. */
export function depositDiscountOffEUR(
  booking: DiscountBookingFields | null
): number {
  return discountOffEUR(
    baseDepositEUR(booking),
    booking?.deposit_discount_mode,
    booking?.deposit_discount_value
  );
}

/**
 * Expected deposit after any discount. Mirrors resolve_expected_deposit_eur in
 * 20260907100000_tenant_rent_deposit_discount.sql — the deposit-paid cascade
 * compares against the same figure.
 */
export function effectiveDepositEUR(
  booking: DiscountBookingFields | null
): number {
  const base = baseDepositEUR(booking);
  return roundRentEUR(Math.max(0, base - depositDiscountOffEUR(booking)));
}

const MONTH_LABELS_EN = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function monthLabel(month: string): string {
  const y = month.slice(0, 4);
  const mi = Number(month.slice(5, 7)) - 1;
  const name = MONTH_LABELS_EN[mi] ?? month.slice(5, 7);
  return `${name} ${y}`;
}

/** "Oct–Dec 2026", "Oct 2026", or "Oct 2026–Jan 2027". */
export function formatMonthRange(start: string, end: string): string {
  if (start === end) return monthLabel(start);
  const sameYear = start.slice(0, 4) === end.slice(0, 4);
  if (!sameYear) return `${monthLabel(start)}–${monthLabel(end)}`;
  const sm =
    MONTH_LABELS_EN[Number(start.slice(5, 7)) - 1] ?? start.slice(5, 7);
  return `${sm}–${monthLabel(end)}`;
}

function fmtEUR(n: number): string {
  // Whole euros unless the amount genuinely has cents, matching how the design
  // canvas renders these figures.
  const rounded = roundRentEUR(n);
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}

/**
 * The admin-facing one-liner, e.g.
 *   "Rent €50 off per month for 3 months (Oct–Dec 2026), then back to €650. Room base stays €650."
 *
 * Admin surfaces only. Never rendered to tenants or owners and never sent in
 * email — the reason and the discount rationale are internal (COL-360).
 */
export function formatDiscountSummary(
  booking: DiscountBookingFields | null,
  options?: { depositLocked?: boolean }
): string | null {
  const hasRent = hasRentDiscount(booking);
  const hasDep = hasDepositDiscount(booking);
  if (!hasRent && !hasDep) return null;

  const parts: string[] = [];
  const baseRent = baseRentEUR(booking);

  if (hasRent) {
    const off = discountOffEUR(
      baseRent,
      booking?.rent_discount_mode,
      booking?.rent_discount_value
    );
    const { start, end } = rentDiscountWindow(booking);
    const newRent = roundRentEUR(Math.max(0, baseRent - off));

    if (end == null) {
      parts.push(
        `Rent €${fmtEUR(off)} off per month for the entire contract → €${fmtEUR(newRent)}.`
      );
    } else {
      const months = Math.floor(Number(booking?.rent_discount_months));
      const unit = months === 1 ? "month" : "months";
      const range = start ? ` (${formatMonthRange(start, end)})` : "";
      parts.push(
        `Rent €${fmtEUR(off)} off per month for ${months} ${unit}${range}, ` +
          `then back to €${fmtEUR(baseRent)}.`
      );
    }
  }

  if (options?.depositLocked) {
    parts.push("Deposit is locked while key handover is pending.");
  } else if (hasDep) {
    const off = depositDiscountOffEUR(booking);
    parts.push(
      `Deposit reduced by €${fmtEUR(off)} to €${fmtEUR(effectiveDepositEUR(booking))}.`
    );
  }

  // Says out loud that the unit's own price did not move — the whole point of
  // keeping base and discount apart.
  if (baseRent > 0) parts.push(`Room base stays €${fmtEUR(baseRent)}.`);

  return parts.join(" ");
}
